import { useEffect, useId, useRef, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { Icon, type IconName } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { CellValue } from '@/lists/cellValue'
import { StateView, type MenuAction } from './record'
import '@/styles/record.css'
import '@/styles/sales-finance-cards.css'

/**
 * Общие части карточек продаж и финансов на дизайн-системе (Lead, Contact, Quote, Product,
 * PrimaryDoc, Contract, Payment .dc.html).
 *
 * Не в `record/`: те компоненты не знают ни запросов, ни ошибок сервера, а здесь — разбор
 * отказа (403 ≠ сбой) и модалка с обязательной причиной, которая нужна отказу по лиду,
 * перевыпуску документа и расторжению договора одинаково.
 */

/** Скелет карточки, пока основной запрос в пути (state=loading макетов). */
export function RecordSkeleton() {
  const { t } = useApp()
  return (
    <div className="rec-page" aria-busy="true" aria-label={t('app.loading')}>
      <span className="skel" style={{ width: 340, height: 20 }} />
      <span className="skel" style={{ height: 44, borderRadius: 6 }} />
      <span className="skel" style={{ height: 64, borderRadius: 8 }} />
      <div className="layout-2-wide">
        <span className="skel" style={{ height: 200, borderRadius: 8 }} />
        <span className="skel" style={{ height: 200, borderRadius: 8 }} />
      </div>
    </div>
  )
}

/**
 * Карточка не открылась: «нет доступа» и «ошибка» — разные состояния с разными
 * действиями. Отказ по правам повторять бессмысленно, сбой — можно; одна красная строка
 * на оба отправляла бы в поддержку с работающим запретом.
 */
export function RecordFailure({
  error,
  onRetry,
  listPath,
}: {
  error: unknown
  onRetry: () => void
  listPath: string
}) {
  const { t } = useApp()
  const forbidden = error instanceof ApiError && error.isForbidden
  return (
    <StateView
      boxed
      kind={forbidden ? 'forbidden' : 'error'}
      title={forbidden ? t('record.state.forbidden.title') : t('record.state.error.title')}
      description={forbidden ? t('record.state.forbidden.text') : t('record.state.error.text')}
      code={error instanceof ApiError ? String(error.status) : undefined}
      actions={
        <>
          {!forbidden && (
            <button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>
              <Icon name="refresh" size="sm" />
              {t('record.state.retry')}
            </button>
          )}
          <Link to={listPath} className="btn btn-ghost btn-sm">
            {t('record.state.toList')}
          </Link>
        </>
      }
    />
  )
}

/** Текст отказа сервера дословно: правило домена пользователь должен прочитать. */
export function failureText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

/** Строка об отказе над вкладками — общая для всех действий карточки. */
export function FailureAlert({ text }: { text: string | null }) {
  if (!text) return null
  return (
    <div className="alert alert-danger" role="alert">
      <Icon name="alert" />
      <span>{text}</span>
    </div>
  )
}

/** Плашка состояния записи: перевод кода и тон — тем же механизмом, что в списке. */
export function StatusBadge({ entity, value, column = 'status' }: { entity: string; value: string | null | undefined; column?: string }) {
  if (!value) return null
  return (
    <CellValue
      values={{ [column]: value }}
      column={column}
      descriptor={{ name: column, type: 'text', sortable: false, filterable: false }}
      entity={entity}
    />
  )
}

/** Пункт «Скопировать ссылку» — первый в каждом меню «⋯». */
export function copyLinkItem(t: (key: string) => string, onFail: (text: string) => void): MenuAction {
  return {
    id: 'copy-link',
    label: t('record.copyLink'),
    icon: 'link',
    onSelect: () => {
      void navigator.clipboard?.writeText(window.location.href).catch(() => onFail(t('record.copyFailed')))
    },
  }
}

/**
 * Модальное окно с полями (CompExtra: `.modal-lg`, `.modal-b.is-form`).
 *
 * Для действий, которым нужны данные: отказ по лиду с причиной, перевыпуск, продление и
 * расторжение договора. Фокус по умолчанию — на «Отмена», как у подтверждения: Enter,
 * нажатый по привычке, не должен ничего закрыть или расторгнуть. Кнопка подтверждения
 * неактивна, пока обязательное не заполнено, а подсказка рядом говорит, чего не хватает,
 * — неактивная кнопка без объяснения выглядит сломанной.
 */
export function FormModal({
  open,
  title,
  overline,
  children,
  confirmLabel,
  onConfirm,
  onCancel,
  canConfirm,
  missingHint,
  pending,
  danger,
  icon,
  failure,
}: {
  open: boolean
  title: ReactNode
  overline?: ReactNode
  children: ReactNode
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
  canConfirm: boolean
  /** Почему подтвердить пока нельзя — рядом с кнопкой. */
  missingHint?: string
  pending?: boolean
  danger?: boolean
  icon?: IconName
  /** Отказ сервера — внутри окна, где его прочтут, а не под ним. */
  failure?: string | null
}) {
  const { t } = useApp()
  const titleId = useId()
  const cancelRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const opener = document.activeElement as HTMLElement | null
    cancelRef.current?.focus()
    return () => opener?.focus?.()
  }, [open])

  if (!open) return null
  return (
    <div
      className="modal-layer"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation()
          onCancel()
        }
      }}
    >
      <div className="modal modal-lg" role={danger ? 'alertdialog' : 'dialog'} aria-modal="true" aria-labelledby={titleId}>
        <div className="modal-h">
          {icon && (
            <span className={danger ? 'modal-ico' : 'modal-ico is-accent'}>
              <Icon name={icon} />
            </span>
          )}
          <div className="stack" style={{ gap: 2, paddingTop: icon ? 5 : 0, flex: 1 }}>
            {overline && <span className="overline">{overline}</span>}
            <span id={titleId} className="h2" style={{ fontSize: 15 }}>
              {title}
            </span>
          </div>
        </div>
        <div className="modal-b is-form" style={{ gap: 14 }}>
          {children}
          {failure && (
            <div className="alert alert-danger" role="alert">
              <Icon name="alert" />
              <span>{failure}</span>
            </div>
          )}
        </div>
        <div className="modal-f" style={{ alignItems: 'center' }}>
          {!canConfirm && missingHint && (
            <span className="t-sm muted" style={{ marginRight: 'auto' }}>
              {missingHint}
            </span>
          )}
          <button ref={cancelRef} type="button" className="btn btn-secondary" onClick={onCancel}>
            {t('card.cancel')}
          </button>
          <button
            type="button"
            className={danger ? 'btn btn-danger' : 'btn btn-primary'}
            disabled={!canConfirm || pending}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Обязательное текстовое поле окна: подпись со звёздочкой, подсказка под полем. */
export function RequiredText({
  label,
  value,
  onChange,
  help,
  placeholder,
  multiline = true,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  help?: string
  placeholder?: string
  multiline?: boolean
}) {
  const id = useId()
  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {label} <span className="req" aria-hidden="true">*</span>
      </label>
      {multiline ? (
        <textarea
          id={id}
          className="input"
          rows={3}
          aria-required="true"
          placeholder={placeholder}
          value={value}
          maxLength={255}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          id={id}
          className="input"
          aria-required="true"
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {help && <span className="help">{help}</span>}
    </div>
  )
}

/** Поле даты окна. */
export function DateField({
  label,
  value,
  onChange,
  required,
  help,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  help?: string
}) {
  const id = useId()
  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {label} {required && <span className="req" aria-hidden="true">*</span>}
      </label>
      <input
        id={id}
        type="date"
        className="input"
        aria-required={required}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {help && <span className="help">{help}</span>}
    </div>
  )
}

/** Сегодня в местном поясе — `YYYY-MM-DD` для поля даты. */
export function todayIso(now: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** Подпись ссылки списка (`<column>__label`) или null. */
export function labelOf(values: Record<string, unknown>, column: string): string | null {
  const label = values[`${column}__label`]
  return typeof label === 'string' && label !== '' ? label : null
}

/** Строковое значение записи списка или null. */
export function textOf(values: Record<string, unknown>, column: string): string | null {
  const value = values[column]
  return typeof value === 'string' && value !== '' ? value : null
}

/** Числовое значение записи списка или null (сервер отдаёт numeric то числом, то строкой). */
export function numberOf(values: Record<string, unknown>, column: string): number | null {
  const value = values[column]
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return Number(value)
  return null
}
